import { DISCOGS_AUTH_PATH } from '../../shared/config/constants'
import {
	NATIVE_AUTH_VALUE_PATTERN,
	NATIVE_SESSION_TOKEN_PATTERN,
} from '../../shared/config/native'
import { t } from '../../shared/config/translations'
import { isHomepagePath } from '../../shared/types/user'
import { resetAuthState } from '../api/auth'
import { showSnackbar } from '../components/Snackbar'
import { apiUrl, IS_NATIVE_APP } from '../config/environment'
import { parseNativeOAuthCallback } from './nativeOAuthCallback'
import {
	getNativeSecureItem,
	removeNativeSecureItem,
	setNativeSecureItem,
} from './nativeSecureStorage'
import { setNativeSessionToken } from './nativeSessionToken'

const PENDING_NATIVE_OAUTH_KEY = 'native-oauth-flow'

type PendingNativeOAuth = {
	state: string
	verifier: string
}

type NativeSessionExchange = {
	homepage: unknown
	token: unknown
}

let nativeOAuthSetup: Promise<void> | null = null
let callbackProcessing: Promise<void> | null = null

/** Starts browser OAuth on web and the app-bound browser flow on native. */
export async function startDiscogsLogin(): Promise<void> {
	if (!IS_NATIVE_APP) {
		window.location.assign(DISCOGS_AUTH_PATH)
		return
	}
	if (callbackProcessing) return
	await ensureNativeOAuthSetup()
	if (callbackProcessing) return

	const verifier = generateNativeAuthValue()
	const state = generateNativeAuthValue()
	const codeChallenge = await deriveCodeChallenge(verifier)
	await writePendingOAuth({ state, verifier })

	const startUrl = new URL(apiUrl('/auth/discogs/native'))
	startUrl.searchParams.set('code_challenge', codeChallenge)
	startUrl.searchParams.set('state', state)

	try {
		const { Browser } = await import('@capacitor/browser')
		await Browser.open({ url: startUrl.toString() })
	} catch (error) {
		await clearPendingOAuth()
		throw error
	}
}

/** Installs warm- and cold-start native deep-link handling once. */
export async function setupNativeDiscogsOAuth(): Promise<void> {
	if (!IS_NATIVE_APP) return
	await ensureNativeOAuthSetup().catch((error: unknown) => {
		console.error('Failed to configure native OAuth callbacks:', error)
	})
}

function ensureNativeOAuthSetup(): Promise<void> {
	nativeOAuthSetup ??= configureNativeOAuth().catch((error: unknown) => {
		nativeOAuthSetup = null
		throw error
	})
	return nativeOAuthSetup
}

async function configureNativeOAuth(): Promise<void> {
	const { App } = await import('@capacitor/app')
	await App.addListener('appUrlOpen', ({ url }) => {
		void processNativeOAuthUrl(url)
	})
	const launch = await App.getLaunchUrl()
	if (launch?.url) await processNativeOAuthUrl(launch.url)
}

async function processNativeOAuthUrl(value: string): Promise<void> {
	if (callbackProcessing) return callbackProcessing
	const processing = handleNativeOAuthUrl(value)
	callbackProcessing = processing
	try {
		await processing
	} catch (error) {
		console.error('Failed to process native OAuth callback:', error)
		showSnackbar(t('auth.loginFailed'), 'error')
	} finally {
		if (callbackProcessing === processing) callbackProcessing = null
	}
}

async function handleNativeOAuthUrl(value: string): Promise<void> {
	const pending = await readPendingOAuth()
	if (!pending) return

	const callback = parseNativeOAuthCallback(value, pending.state)
	if (!callback) return

	try {
		const { Browser } = await import('@capacitor/browser')
		await Browser.close().catch(() => undefined)

		if (callback.type === 'error') {
			showSnackbar(t('auth.loginFailed'), 'error')
			return
		}

		const response = await fetch(apiUrl('/auth/native/exchange'), {
			body: JSON.stringify({
				code: callback.code,
				verifier: pending.verifier,
			}),
			headers: { 'Content-Type': 'application/json' },
			method: 'POST',
		})
		if (!response.ok) {
			throw new Error(
				`Native auth exchange failed with ${response.status}.`,
			)
		}

		const session = (await response.json()) as NativeSessionExchange
		if (
			typeof session.token !== 'string' ||
			!NATIVE_SESSION_TOKEN_PATTERN.test(session.token)
		) {
			throw new Error('Native auth exchange returned an invalid session.')
		}

		await setNativeSessionToken(session.token)
		resetAuthState()
		const homepage = isHomepagePath(session.homepage)
			? session.homepage
			: '/collection'
		const { navigateTo } = await import('../router')
		history.replaceState(null, '', homepage)
		await navigateTo(homepage)
	} catch (error) {
		console.error('Native Discogs login failed:', error)
		showSnackbar(t('auth.loginFailed'), 'error')
	} finally {
		await clearPendingOAuth()
	}
}

async function readPendingOAuth(): Promise<PendingNativeOAuth | null> {
	const stored = await getNativeSecureItem(PENDING_NATIVE_OAUTH_KEY)
	if (!stored) return null

	try {
		const value = JSON.parse(stored) as Partial<PendingNativeOAuth>
		const pending =
			NATIVE_AUTH_VALUE_PATTERN.test(value.state ?? '') &&
			NATIVE_AUTH_VALUE_PATTERN.test(value.verifier ?? '')
				? (value as PendingNativeOAuth)
				: null
		if (!pending) await clearPendingOAuth()
		return pending
	} catch {
		await clearPendingOAuth()
		return null
	}
}

async function writePendingOAuth(value: PendingNativeOAuth): Promise<void> {
	await setNativeSecureItem(PENDING_NATIVE_OAUTH_KEY, JSON.stringify(value))
}

async function clearPendingOAuth(): Promise<void> {
	try {
		await removeNativeSecureItem(PENDING_NATIVE_OAUTH_KEY)
	} catch (error) {
		console.error('Failed to clear pending native OAuth state:', error)
	}
}

function generateNativeAuthValue(): string {
	const bytes = crypto.getRandomValues(new Uint8Array(32))
	return bytesToBase64Url(bytes)
}

async function deriveCodeChallenge(verifier: string): Promise<string> {
	const digest = await crypto.subtle.digest(
		'SHA-256',
		new TextEncoder().encode(verifier),
	)
	return bytesToBase64Url(new Uint8Array(digest))
}

function bytesToBase64Url(bytes: Uint8Array): string {
	return btoa(String.fromCharCode(...bytes))
		.replace(/\+/g, '-')
		.replace(/\//g, '_')
		.replace(/=+$/, '')
}
